import { useParams, useNavigate } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import PageLoader from '@/components/common/PageLoader';
import EditorErrorState from '@/components/editors/EditorErrorState';
import { ContentEditor } from '@/components/editors/ContentEditor';
import type { ContentEditorEvent } from '@/services/editors/content-editor';
import { useContentRead } from '@/hooks/useContent';
import { useAppI18n } from '@/hooks/useAppI18n';
import { useEditorLock } from '@/hooks/useEditorLock';
import useImpression from '@/hooks/useImpression';
import useInteract from '@/hooks/useInteract';
import { useEditorBackNavigation } from '@/pages/workspace/editors/useEditorBackNavigation';

const ContentEditorPage = () => {
  const { t } = useAppI18n();
  const { contentId } = useParams();
  const navigate = useNavigate();
  const { interact } = useInteract();

  useImpression({ type: 'view', pageid: 'content-editor', object: { id: contentId || '', type: 'Content' } });

  const { data, isLoading, error } = useContentRead(contentId || '');
  const contentData = data?.data?.content;

  const { lockError, isLocking, retireLock } = useEditorLock({
    contentId,
    metadata: contentData ?? null,
  });

  const backTo = useEditorBackNavigation();

  // Memoize the event handler to prevent unnecessary re-renders
  const handleEditorEvent = useCallback((event: ContentEditorEvent) => {
    interact({
      id: 'content-editor-event',
      type: 'OTHER',
      pageid: 'content-editor',
      object: { id: event.type, type: 'Event' },
      cdata: [{ id: contentId || '', type: 'ContentId' }],
    });
    console.warn('Content editor event:', event);
  }, [interact, contentId]);

  // Memoize the close handler to prevent unnecessary re-renders
  const handleClose = useCallback(async () => {
    await retireLock();
    navigate(backTo);
  }, [retireLock, navigate, backTo]);

  // Memoize contentData to prevent unnecessary re-renders when object reference changes
  // but actual content hasn't changed
  const stableMetadata = useMemo(() => contentData, [contentData?.identifier]);

  if (isLoading || isLocking) {
    return <PageLoader message={isLocking ? "Acquiring content lock..." : "Loading editor..."} />;
  }

  if (lockError) {
    return <EditorErrorState message={lockError} />;
  }

  if (error) {
    return <EditorErrorState message={`Error loading content: ${error.message}`} showRetry />;
  }

  if (!stableMetadata) {
    return <EditorErrorState message="Content not found" />;
  }

  return (
    <div className="w-full h-screen">
      <ContentEditor
        metadata={stableMetadata}
        onEditorEvent={handleEditorEvent}
        onClose={handleClose}
      />
    </div>
  );
};

export default ContentEditorPage;
